import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { appContext } from 'common/contexts';
import { Routes as R } from 'common/enums';
import { PlusTwoTone } from 'common/icons';
import { InventoryItem, StockMovement } from 'common/types';
import InventoryNav from '../inventoryItemsList/inventoryNav';
import styles from './stockMovementsList.module.css';

type FilterState = {
  type: string;
  itemId: string;
  startDate: string;
  endDate: string;
};

const StockMovementsList: React.FC = () => {
  const { state, helpers } = useContext(appContext);
  const { stockMovements = [], inventoryItems = [] } = state;
  const { translation } = helpers;
  const { t } = translation;
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  
  // Forzar actualización cuando cambian los movimientos de stock
  useEffect(() => {
    console.log('Movimientos de stock actualizados en el componente:', stockMovements.length, 'movimientos');
    setLastUpdate(Date.now());
  }, [stockMovements]);

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    type: '',
    itemId: '',
    startDate: '',
    endDate: ''
  });

  // Active inventory items (not deleted)
  const activeItems = inventoryItems.filter(item => !item.isDeleted);

  // Apply filters to stock movements
  const filteredMovements = stockMovements.filter(movement => {
    // Filter by type
    if (filters.type && movement.type !== filters.type) {
      return false;
    }

    // Filter by item
    if (filters.itemId && movement.inventoryItemId !== filters.itemId) {
      return false;
    }

    // Filter by date range
    if (filters.startDate) {
      const startTimestamp = new Date(filters.startDate).getTime();
      if (movement.date < startTimestamp) {
        return false;
      }
    }

    if (filters.endDate) {
      const endTimestamp = new Date(filters.endDate + 'T23:59:59').getTime();
      if (movement.date > endTimestamp) {
        return false;
      }
    }

    return true;
  });

  // Sort movements by date (newest first)
  const sortedMovements = [...filteredMovements].sort((a, b) => b.date - a.date);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      itemId: '',
      startDate: '',
      endDate: ''
    });
  };

  const getItemName = (itemId: string) => {
    const item = inventoryItems.find(item => item.id === itemId);
    return item ? item.name : t('common.deletedItem');
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'purchase':
        return styles.purchase;
      case 'consumption':
        return styles.consumption;
      case 'sale':
        return styles.sale;
      case 'adjustment':
        return styles.adjustment;
      default:
        return '';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'purchase':
        return t('admin.inventory.movements.purchase');
      case 'consumption':
        return t('admin.inventory.movements.consumption');
      case 'sale':
        return t('admin.inventory.movements.sale');
      case 'adjustment':
        return t('admin.inventory.movements.adjustment');
      default:
        return type;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('admin.inventory.title')}</h1>
      </div>
      
      <InventoryNav />
      
      <div className={styles.header}>
        <h2 className={styles.title}>{t('admin.inventory.movements.title')}</h2>
        <Link to={R.StockMovementCreate} className={styles.addButton}>
          <PlusTwoTone className={styles.addButtonIcon} />
          {t('admin.inventory.movements.addMovementLabel')}
        </Link>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>{t('admin.inventory.movements.type')}</label>
          <select
            name="type"
            className={styles.filterSelect}
            value={filters.type}
            onChange={handleFilterChange}
          >
            <option value="">{t('common.all')}</option>
            <option value="purchase">{t('admin.inventory.movements.purchase')}</option>
            <option value="consumption">{t('admin.inventory.movements.consumption')}</option>
            <option value="sale">{t('admin.inventory.movements.sale')}</option>
            <option value="adjustment">{t('admin.inventory.movements.adjustment')}</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>{t('admin.inventory.movements.item')}</label>
          <select
            name="itemId"
            className={styles.filterSelect}
            value={filters.itemId}
            onChange={handleFilterChange}
          >
            <option value="">{t('common.all')}</option>
            {activeItems.map(item => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>{t('common.from')}</label>
          <input
            type="date"
            name="startDate"
            className={styles.filterInput}
            value={filters.startDate}
            onChange={handleFilterChange}
          />
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>{t('common.to')}</label>
          <input
            type="date"
            name="endDate"
            className={styles.filterInput}
            value={filters.endDate}
            onChange={handleFilterChange}
          />
        </div>

        <button className={styles.clearFilters} onClick={clearFilters}>
          {t('common.clear')}
        </button>
      </div>

      <div className={styles.tableContainer}>
        {sortedMovements.length > 0 ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.inventory.movements.date')}</th>
                <th>{t('admin.inventory.movements.type')}</th>
                <th>{t('admin.inventory.movements.item')}</th>
                <th>{t('admin.inventory.movements.quantity')}</th>
                <th>{t('admin.inventory.movements.reference')}</th>
                <th>{t('admin.inventory.movements.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedMovements.map((movement) => (
                <tr key={`${movement.id}-${lastUpdate}`}>
                  <td>{formatDate(movement.date)}</td>
                  <td>
                    <span className={`${styles.typeBadge} ${getTypeBadgeClass(movement.type)}`}>
                      {getTypeLabel(movement.type)}
                    </span>
                  </td>
                  <td>{getItemName(movement.inventoryItemId)}</td>
                  <td className={`${movement.quantity >= 0 ? styles.positive : styles.negative} ${styles.quantityCell}`}>
                    {movement.quantity > 0 ? '+' : ''}{parseFloat(movement.quantity.toString()).toFixed(2)}
                  </td>
                  <td>{movement.reference || '-'}</td>
                  <td>{movement.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📈</div>
            <p>{t('admin.inventory.movements.emptyListMessage')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockMovementsList;
